import React, { useRef, useState } from "react";
import jsonImage from "../../../../assets/images/json.svg";
import { error } from "../../../../helpers/notifications";
import IconButton from "../../../common/icon-button/icon-button";
import ModalDefault from "../../../common/modal/modal-default";
import "./import-dashboards-window.scss";
import {
  IConfigurationDashboardProps,
  IConfigurationDashboardData,
} from "./models/configuration-dashboard.props";
import { checkStructure } from "../../../../helpers/dashboard-helper";

const ImportDashboardsWindow = ({ visible, onClose, onImportDashboards }) => {
  const fileInputField = useRef(null);
  const [jsonDashboards, setJsonDashboards] = useState([]);
  const [files, setFiles] = useState([]);

  const onReaderLoad = (event) => {
    try {
      const json = JSON.parse(event.target.result);
      if (!Array.isArray(json)) {
        return;
      }
      const fileName = event.target.fileName;

      setFiles((oldFiles) =>
        !oldFiles.includes(fileName) ? [...oldFiles, fileName] : oldFiles
      );

      setJsonDashboards((oldJsonDashboards) => {
        const newJsonDashboards = json.map((item) => ({ ...item, fileName }));
        return [...oldJsonDashboards, ...newJsonDashboards];
      });
    } catch (e) {
      error({
        name: "Error! ",
        message: `The file was changed and cannot be imported.`,
      });
    }
  };

  const onDeleteFile = (fileName) => {
    setFiles((oldFiles) => oldFiles.filter((name) => name !== fileName));

    setJsonDashboards((dashboards) =>
      dashboards.filter((dashboard) => dashboard.fileName !== fileName)
    );
  };

  const readFile = (file) => {
    const reader = new FileReader();
    reader.fileName = file.name;
    reader.onload = onReaderLoad;
    reader.readAsText(file);
  };

  const onChange = (event) => {
    if (event.target.files[0].type !== "application/json") {
      error({
        name: "Error! ",
        message: "The file is not in JSON format and cannot be imported.",
      });
      return;
    }
    const file = event.target.files[0];
    readFile(file);
  };

  const onDrop = (ev) => {
    ev.preventDefault();

    if (!ev.dataTransfer.items) {
      return;
    }

    for (let i = 0; i < ev.dataTransfer.items.length; i++) {
      if (
        ev.dataTransfer.items[i].kind === "file" &&
        ev.dataTransfer.items[i].type === "application/json"
      ) {
        const file = ev.dataTransfer.items[i].getAsFile();
        readFile(file);
      } else {
        error({
          name: "Error! ",
          message: "The file is not in JSON format and cannot be imported.",
        });
      }
    }
  };

  const dragOverHandler = (ev) => {
    ev.preventDefault();
  };

  return (
    <ModalDefault isOpen={visible} customClass="import-window__container">
      <div className="glu-modal__header glu-flex glu-flex-align-center">
        <div className="glu-modal__title glu-flex-grow">Import Dashboards</div>
        <div className="glu-flex-shrink">
          <button
            aria-label="close"
            type="button"
            className="glu-btn-link g-icon g-icon-close"
            onClick={onClose}
          />
        </div>
      </div>
      <div className="glu-modal__body import-window__body">
        <label
          htmlFor="upload-json"
          className="import-window__input-container"
          id="drop_zone"
          onDrop={onDrop}
          onDragOver={dragOverHandler}
        >
          <input
            type="file"
            onChange={onChange}
            ref={fileInputField}
            title=""
            accept=".json"
            value=""
            className="import-window__input"
            id="upload-json"
          />
          <label htmlFor="upload-json" className="import-window__label">
            <IconButton icon="touch_app" className="import-window__button" />
            Drag 'n' drop dashboard file to import, or click to select files
          </label>
        </label>
        <div className="import-window__files-container">
          {files.map((fileName) => {
            return (
              <div key={fileName} className="import-window__img-container">
                <div className="import-window__img-wrap">
                  <span
                    className="close-button"
                    onClick={() => onDeleteFile(fileName)}
                  >
                    &times;
                  </span>
                  <img
                    src={jsonImage}
                    className="import-window__json-image"
                    alt=""
                  />
                </div>
                <div className="text-ellipsis import-window__file-name">
                  {fileName}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <footer className="glu-modal__footer">
        <div className="glu-flex-grow glu-flex-row">
          <div className="glu-flex-grow text-right">
            <button
              type="button"
              className="glu-btn glu-btn--secondary"
              onClick={onClose}
            >
              Cancel
            </button>
          </div>
          <div className="glu-flex-shrink">
            <button
              className="glu-btn glu-btn--primary"
              type="submit"
              onClick={() => {
                const filteredByIdDashboardsObject = jsonDashboards.reduce(
                  (acc, item) => {
                    return {
                      ...acc,
                      [item.id]: item,
                    };
                  },
                  {}
                );

                const filteredDashboards = Object.keys(
                  filteredByIdDashboardsObject
                ).map((item) => {
                  const { fileName, ...keepAttrs } =
                    filteredByIdDashboardsObject[item];
                  const response = checkStructure(keepAttrs);
                  if (!checkStructure(keepAttrs)) {
                    error({
                      name: "Error! ",
                      message:
                        "This is not a valid dashboard JSON format and cannot be imported.",
                    });
                  }
                  return keepAttrs;
                });

                onImportDashboards(filteredDashboards);

                onClose();
              }}
              disabled={!files.length}
            >
              Import
            </button>
          </div>
        </div>
      </footer>
    </ModalDefault>
  );
};

export default ImportDashboardsWindow;
